/* Shared photo readiness. Eager/cached images and transparent artwork keep their first paint. */
(() => {
  if (window.ChaponImages) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const pending = new WeakSet();
  const animations = new Set();
  const artwork = image => /steam|wash|illus|logo|fg-apply__img/.test(image.className + ' ' + image.getAttribute('src'));
  const ready = image => {
    if (!image) return Promise.resolve();
    return (image.complete ? Promise.resolve() : new Promise(resolve => {
      const done = () => { image.removeEventListener('load', done); image.removeEventListener('error', done); resolve(); };
      image.addEventListener('load', done);
      image.addEventListener('error', done);
    })).then(() => image.decode()).catch(() => {});
  };
  const preload = image => {
    if (image) {
      // Images cloned from a template still belong to its inert document.
      if (image.ownerDocument && image.ownerDocument !== document) document.adoptNode(image);
      image.loading = 'eager';
    }
    return ready(image);
  };
  window.ChaponImages = { ready, preload };

  const reveal = async (image, failed = false) => {
    if (!pending.has(image)) return;
    if (!failed) await ready(image);
    if (!pending.has(image)) return;
    pending.delete(image);
    // Install the animation before removing the pending state, so decoded pixels never pop first.
    if (!failed && image.naturalWidth && !motion.matches && !image.matches('.card__media img + img') && typeof image.animate === 'function') {
      const animation = image.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' });
      image.classList.add('image-revealing');
      animations.add(animation);
      const done = () => { animations.delete(animation); image.classList.remove('image-revealing'); };
      animation.finished.then(done, done);
    }
    image.classList.remove('image-pending');
  };
  document.addEventListener('load', event => { if (event.target instanceof HTMLImageElement) reveal(event.target); }, true);
  document.addEventListener('error', event => { if (event.target instanceof HTMLImageElement) reveal(event.target, true); }, true);
  const enhance = root => {
    const images = root instanceof HTMLImageElement ? [root] : root.querySelectorAll?.('img') || [];
    for (const image of images) {
      if (image.loading !== 'lazy' || image.complete || artwork(image) || pending.has(image) || motion.matches) continue;
      pending.add(image);
      image.classList.add('image-pending');
      // A load can have completed between the test and listener setup (including cached insertions).
      if (image.complete) reveal(image, !image.naturalWidth);
    }
  };
  enhance(document);
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) enhance(node);
  }).observe(document, { childList: true, subtree: true });
  motion.addEventListener('change', () => {
    if (!motion.matches) return;
    document.querySelectorAll('.image-pending').forEach(image => { pending.delete(image); image.classList.remove('image-pending'); });
    animations.forEach(animation => animation.finish());
  });
  document.dispatchEvent(new CustomEvent('chapon:images-ready'));
})();
